import { useState, useEffect, useCallback } from 'react';
import { Video, VideosApiResponse, VideoApiItem, ApiError } from '../types';

interface UseTopVideosResult {
  videos: Video[];
  isLoading: boolean;
  error: string | null;
  isCached: boolean;
  fetchedAt: string | null;
  refetch: () => void;
}

/**
 * Convert seconds to ISO 8601 duration format (for backwards compat)
 */
function secondsToDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let result = 'PT';
  if (hours > 0) result += `${hours}H`;
  if (minutes > 0) result += `${minutes}M`;
  if (secs > 0 || result === 'PT') result += `${secs}S`;
  return result;
}

/**
 * Map new API response to Video format
 */
function mapApiVideo(item: VideoApiItem): Video {
  // Use actual publishedAt if available, otherwise fall back to lastSnapshotAt
  const publishedTimestamp = item.publishedAt ?? item.lastSnapshotAt;

  return {
    id: item.videoId,
    title: item.title,
    channelTitle: item.channelTitle,
    channelId: item.channelId,
    thumbnail: item.thumbnailUrl,
    viewCount: item.viewCount,
    publishedAt: new Date(publishedTimestamp * 1000).toISOString(),
    duration: secondsToDuration(item.durationSeconds),
    durationSeconds: item.durationSeconds,
    deltaViews: item.deltaViews,
    deltaPerHour: item.deltaPerHour,
    isShort: item.isShort,
  };
}

/**
 * Custom hook to fetch top videos from the D1-backed API
 */
export function useTopVideos(category: string, period: string, region: string = 'US', enabled = true): UseTopVideosResult {
  const [videos, setVideos] = useState<Video[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [isCached, setIsCached] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);

  const fetchVideos = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    setError(null);

    try {
      // Map category '0' to 'ALL' for backwards compatibility
      const categoryId = category === '0' ? 'ALL' : category;

      const params = new URLSearchParams({
        region,
        category: categoryId,
        time: period,
        shorts: 'true',
        limit: '100',
      });

      const response = await fetch(`/api/videos?${params.toString()}`);

      if (!response.ok) {
        const errorData = (await response.json()) as ApiError;
        throw new Error(errorData.error || 'Failed to fetch videos');
      }

      const data = (await response.json()) as VideosApiResponse;
      const mappedVideos = data.videos.map(mapApiVideo);

      setVideos(mappedVideos);
      setIsCached(data.cached);
      setFetchedAt(new Date(data.generatedAt).toISOString());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      setVideos([]);
    } finally {
      setIsLoading(false);
    }
  }, [category, period, region, enabled]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  return {
    videos,
    isLoading,
    error,
    isCached,
    fetchedAt,
    refetch: fetchVideos,
  };
}

